import React, {useEffect, useState} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import {
    getDeviceScreenRecommendation,
    VIEWPORT,
    type DeviceScreenRecommendation,
} from "../../utils/deviceScreen";

const MOBILE_TITLE = "Mithra works best on a larger screen";
const MOBILE_MESSAGE ="Mithra is not currently supported on mobile devices. Please use a laptop or desktop to access the system."
const TABLET_TITLE = "Screen size recommendation";
const TABLET_MESSAGE =
    "Recommend using a laptop instead, since the application is primarily designed for larger screens. Alternatively, rotate your device to landscape mode for a better experience.";

const DISMISSED_TABLET_DIALOG_KEY = "small-screen-recommendation-dismissed";

function hasDismissedTabletDialog(): boolean {
    return localStorage.getItem(DISMISSED_TABLET_DIALOG_KEY) === "true";
}

function persistDismissedTabletDialog(): void {
    localStorage.setItem(DISMISSED_TABLET_DIALOG_KEY, "true");
}

export const SmallScreenRecommendationDialog: React.FC = () => {
    const [recommendation, setRecommendation] = useState<DeviceScreenRecommendation>(() =>
        getDeviceScreenRecommendation(),
    );
    const [dismissedTabletDialog, setDismissedTabletDialog] = useState(hasDismissedTabletDialog);

    useEffect(() => {
        const updateRecommendation = () => {
            setRecommendation(getDeviceScreenRecommendation());
        };

        window.addEventListener("resize", updateRecommendation);
        window.addEventListener("orientationchange", updateRecommendation);

        return () => {
            window.removeEventListener("resize", updateRecommendation);
            window.removeEventListener("orientationchange", updateRecommendation);
        };
    }, []);

    const isBlocking = recommendation === VIEWPORT.mobile;
    const isClosable =
        recommendation === VIEWPORT.tablet && !dismissedTabletDialog;
    const open = isBlocking || isClosable;

    const title = isBlocking ? MOBILE_TITLE : TABLET_TITLE;
    const message = isBlocking ? MOBILE_MESSAGE : TABLET_MESSAGE;

    const handleClose = () => {
        if (isClosable) {
            persistDismissedTabletDialog();
            setDismissedTabletDialog(true);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableEscapeKeyDown={isBlocking}
            aria-labelledby="small-screen-recommendation-title"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="small-screen-recommendation-title" sx={{pb: 1}}>
                {title}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>{message}</DialogContentText>
            </DialogContent>
            {isClosable && (
                <DialogActions>
                    <Button onClick={handleClose} variant="contained">
                        Got it
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
